/* @flow */
import EventClass from 'lib/EventClass';

/**
 * Monitors whether the browser window is in focus and current document is active
 */
class BrowserVisibility extends EventClass {
  /**
   * @param  {object} config - configuration object to set specific settings in the instance
   * @param {object} ...params - object of the rest of the parameters to easily pass to
   * super consturctor
   */
  constructor(config = {}, ...params) {
    super(config, ...params);

    (this: any).setupEventListeners = this.setupEventListeners.bind(this);
    (this: any).releaseEventListeners = this.releaseEventListeners.bind(this);
    (this: any).documentVisibilityChange = this.documentVisibilityChange.bind(this);
    (this: any).windowFocusEvent = this.windowFocusEvent.bind(this);
    (this: any).windowBlurEvent = this.windowBlurEvent.bind(this);
    (this: any).triggerStateChangeEvent = this.triggerStateChangeEvent.bind(this);

    this._config = {
      ...config,
    };

    this._visibilityState = (typeof document.hasFocus === 'function') ?
      document.hasFocus() : (document.visibilityState === 'visible');

    this.setupEventListeners();
  }

  /**
   * Setup event listeners that trigger active state changes
   * @private
   */
  setupEventListeners() {
    document.addEventListener('visibilitychange', this.documentVisibilityChange);
    window.addEventListener('focus', this._windowFocusEvent);
    window.addEventListener('blur', this._windowBlurEvent);
  }

  /**
   * Release event listeners that trigger active state changes
   * @private
   */
  releaseEventListeners() {
    document.removeEventListeners('visibilitychange', this.documentVisibilityChange);
    window.removeEventListener('focus', this._windowFocusEvent);
    window.removeEventListeners('blur', this._windowBlurEvent);
  }

  /**
   * Internal callback for document visibility change event
   * @private
   */
  documentVisibilityChange() {
    this.triggerStateChangeEvent(document.visibilityState === 'visible', { srcEvent: 'documentVisibilityChange' });
  }

  /**
   * Internal callback for window focus event
   * @private
   */
  windowFocusEvent() {
    this.triggerStateChangeEvent(true, { srcEvent: 'windowFocus' });
  }

  /**
   * Internal callback for window blur event
   * @private
   */
  windowBlurEvent() {
    this.triggerStateChangeEvent(false, { srcEvent: 'windowBlur' });
  }

  /**
   * Common event creator for different event callbacks
   * @private
   * @param  {boolean} state - new active state
   * @param  {Object} extraParams - any other parameters that might need to be passed to callbacks
   */
  triggerStateChangeEvent(state, extraParams = {}) {
    this._visibilityState = state;
    this.dispatchEvent('statechange', this._visibilityState, extraParams);
  }

  /**
   * Get the current active state of the activity
   * @return {boolean}
   */
  get active() {
    this._visibilityState = (typeof document.hasFocus === 'function') ?
      document.hasFocus() : (document.visibilityState === 'visible');
    return this._visibilityState;
  }

  /**
   * Cleanup any event listeners and variable references so it can be cleanly released
   * @param  {...object} params - any unloading params that might be passed to super's
   * unload function
   */
  unload(...params) {
    this.releaseEventListeners();
    super.unload(...params);
  }
}

export default BrowserVisibility;
